import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MARKETPLACE_ERROR_HANDLERS, WalletPaymentMethodType } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { MobileService } from '@platform-frontend/ui-tools';
import { handleAllErrors, handleErrors, ShowErrorNotification } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { catchError, combineLatest, EMPTY, filter, from, Observable, of, Subscriber, take, tap, timeout } from 'rxjs';
import { MSG_WALLET_PAYMENT_APPLE_DEFAULT } from '../notification-messages';
import { getGooglePayEnvironment, GOOGLE_PAY_API_VERSION, GOOGLE_PAY_MERCHANT_INFO, GOOGLE_PAY_PAYMENT_METHOD_SPECIFICATION } from './google-pay-config';
import { PaymentWalletStateModel } from './model/payment-wallet-state-model';
import { CheckWalletAutoSelection, CheckWalletSupport, ValidateApplePaySession } from './payment-wallet.actions';

const WALLET_API_REQUEST_TIMEOUT = 5000;

const DEFAULT_STATE: PaymentWalletStateModel = {
  checkedWallets: [],
  enabledWallets: [],
  autoselectableWallet: null
};

@State<PaymentWalletStateModel>({
  name: 'paymentWallet',
  defaults: DEFAULT_STATE
})
@Injectable()
export class PaymentWalletState {
  constructor(@Inject(DOCUMENT) private document: Document, private http: HttpClient, rendererFactory: RendererFactory2, private mobileService: MobileService) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  private renderer: Renderer2;

  @Selector()
  static enabledWallets(state: PaymentWalletStateModel): WalletPaymentMethodType[] {
    return state.enabledWallets;
  }

  @Selector()
  static autoselectableWallet(state: PaymentWalletStateModel): WalletPaymentMethodType | null {
    return state.autoselectableWallet;
  }

  @Action(CheckWalletSupport)
  checkWalletSupport(ctx: StateContext<PaymentWalletStateModel>, { allowedPaymentMethods, isProductLive }: CheckWalletSupport): Observable<unknown> {
    const checkedWallets = ctx.getState().checkedWallets;

    let applePayCheck$: Observable<unknown> = of(null);
    let googlePayCheck$: Observable<unknown> = of(null);

    if (allowedPaymentMethods.includes('APPLE_PAY') && !checkedWallets.includes('APPLE_PAY')) {
      applePayCheck$ = new Observable<void>(observer => this.initializeApplePay(ctx, observer)).pipe(
        tap(() => ctx.patchState({ checkedWallets: [...ctx.getState().checkedWallets, 'APPLE_PAY'] })),
        catchError(() => EMPTY)
      );
    }

    if (allowedPaymentMethods.includes('GOOGLE_PAY') && !checkedWallets.includes('GOOGLE_PAY')) {
      googlePayCheck$ = new Observable(observer => this.initializeGooglePay(ctx, observer, isProductLive)).pipe(
        tap(() => ctx.patchState({ checkedWallets: [...ctx.getState().checkedWallets, 'GOOGLE_PAY'] })),
        catchError(() => EMPTY)
      );
    }

    return combineLatest([applePayCheck$, googlePayCheck$]);
  }

  private initializeApplePay(ctx: StateContext<PaymentWalletStateModel>, observer: Subscriber<void>): void {
    const script = this.renderer.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.onload = () => {
      if (!ApplePaySession) {
        observer.error();
      }
      if (ApplePaySession.canMakePayments()) ctx.patchState({ enabledWallets: [...ctx.getState().enabledWallets, 'APPLE_PAY'] });
      observer.next();
      observer.complete();
    };
    script.onerror = (err: any) => {
      observer.error(err);
    };
    this.renderer.appendChild(this.document.body, script);
  }

  private initializeGooglePay(ctx: StateContext<PaymentWalletStateModel>, observer: Subscriber<void>, isProductLive: boolean): void {
    const script = this.renderer.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.onload = () => {
      from(
        new google.payments.api.PaymentsClient({
          environment: getGooglePayEnvironment(PLATFORM_ENVIRONMENT.isProduction && isProductLive),
          merchantInfo: GOOGLE_PAY_MERCHANT_INFO
        }).isReadyToPay({ ...GOOGLE_PAY_API_VERSION, allowedPaymentMethods: GOOGLE_PAY_PAYMENT_METHOD_SPECIFICATION })
      )
        .pipe(
          take(1),
          timeout({ first: WALLET_API_REQUEST_TIMEOUT }),
          filter(response => response.result),
          tap(() => {
            ctx.patchState({ enabledWallets: [...ctx.getState().enabledWallets, 'GOOGLE_PAY'] });
            observer.next();
            observer.complete();
          }),
          catchError(() => {
            observer.error();
            return EMPTY;
          })
        )
        .subscribe();
    };
    script.onerror = (err: any) => {
      observer.error(err);
    };
    this.renderer.appendChild(this.document.body, script);
  }

  @Action(CheckWalletAutoSelection)
  checkWalletAutoSelection(ctx: StateContext<PaymentWalletStateModel>): Observable<unknown> {
    return this.mobileService.isAppleDevice() && ctx.getState().enabledWallets.includes('APPLE_PAY')
      ? from(ApplePaySession.applePayCapabilities('merchant.com.yatta.checkout')).pipe(
          take(1),
          timeout({ first: WALLET_API_REQUEST_TIMEOUT }),
          filter(capabilities => capabilities.paymentCredentialStatus === 'paymentCredentialsAvailable'),
          tap(() => ctx.patchState({ autoselectableWallet: 'APPLE_PAY' })),
          catchError(() => EMPTY)
        )
      : of(null);
  }

  @Action(ValidateApplePaySession)
  validateApplePaySession(ctx: StateContext<PaymentWalletStateModel>, { pricingInformation, session }: ValidateApplePaySession): Observable<unknown> {
    return this.http.post<any>(PLATFORM_ENVIRONMENT.apiUrl + '/payment/apple-pay/session', pricingInformation).pipe(
      tap(merchantSession => session.completeMerchantValidation(JSON.parse(Buffer.from(merchantSession.data, 'base64').toString()))),
      handleAllErrors(() => session.abort()),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, { DEFAULT: new ShowErrorNotification(MSG_WALLET_PAYMENT_APPLE_DEFAULT) })
    );
  }
}
